import React, { useLayoutEffect, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./Finder.module.css";
import {
  PatternlibButton,
  PatternlibIcon,
  PatternlibSpinner,
} from "@liebherr/patternlib-react";
import { apiURL, apiKey } from "../../config/api";
import {
  getTableData,
  getSearchResult,
  getTableDataOnChange,
  getLanguage,
} from "../../helpers/helpers";
import BackButton from "../../components/back-button/BackButton";
import Dropdown from "../../components/dropdown/Dropdown.js";
import Slider from "../../components/slider/Slider";
import ImageSelector from "../../components/image-selector/ImageSelector";
import Separator from "../../components/separator/Separator.js";
import NotRightProductDiv from "../../components/not-right-product-div/NotRightProductDiv";
import { useNavigate } from "react-router-dom";
import image260bar from "../../img/260bar.png";
import image380bar from "../../img/380bar.png";
import imageIso6022 from "../../img/iso6022.png";
import Input from "../../components/input/Input";
import useMutationObserver from "../../hooks/useMutationObserver";

const Finder = ({ isDemoUser }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState();
  const [value, setValue] = useState({});
  const [nodes, setNodes] = useState([]);
  const [isSearching, setIsSearching] = useState(true);

  // the useRef Hook allows to persist data between renders
  const varsetRef = useRef();

  const path = "liebherr_components%2Fsearch_assistent.prj";
  const configKeys = ["PA", "MT", "FN", "F"];

  const navigate = useNavigate();

  // use mutation observer to get current screen size
  const options = {
    config: { attributes: true, childList: true, subtree: true },
  };
  const target = document.documentElement;
  const attributeName = "breakpoint";
  const screen = useMutationObserver(options, target, attributeName);

  const getTable = () => {
    const mident = "";
    const lines = null;
    const showAll = true;

    getTableData(
      apiURL,
      path,
      apiKey,
      getLanguage(i18n.language),
      mident,
      lines,
      showAll
    ).then((data) => {
      if (data !== undefined) {
        const varset = data.index.varsettransfer;
        const values = data.index.line.values;
        const query = data.index.line.values["SEARCH"].current;
        varsetRef.current = varset;
        setData(values);
        getSearchResult(apiURL, apiKey, query).then((data) => {
          if (data !== undefined) {
            const nodes = data.index.nodes;
            updateNodes(nodes);
            setIsSearching(false);
          }
        });
      }
    });
  };

  const getTableChange = async (variable, value, varset) => {
    const showAll = true;

    getTableDataOnChange(
      apiURL,
      path,
      apiKey,
      getLanguage(i18n.language),
      variable,
      value,
      varset,
      null,
      null,
      showAll
    ).then((data) => {
      if (data !== undefined) {
        const varset = data.tableData.index.varsettransfer;
        const values = data.tableData.index.line.values;
        const query = data.tableData.index.line.values["SEARCH"].current;
        varsetRef.current = varset;
        setData(values);
        getSearchResult(apiURL, apiKey, query).then((data) => {
          if (data !== undefined) {
            const nodes = data.index.nodes;
            updateNodes(nodes);
            setIsSearching(false);
          }
        });
      }
    });
  };

  const updateValues = () => (e) => {
    const variable = e.variable;
    const value = e.value;
    setValue({ variable, value });
  };

  const resetValues = () => () => {
    setIsSearching(true);
    getTable();
  };

  const updateNodes = (nodes) => {
    const paths = ["series260", "series380", "iso6022"];
    const arr = [];

    if (nodes !== null) {
      paths.map((path) => {
        const series = nodes.filter((el) => el.path.indexOf(path) > 0);
        if (series.length > 0) {
          arr.push({ ...series[0], path });
        }
      });
    }
    setNodes(arr);
  };

  useEffect(() => {
    if (Object.keys(value).length >= 1) {
      setIsSearching(true);
      getTableChange(value["variable"], value["value"], varsetRef.current);
    }
  }, [value]);

  useLayoutEffect(() => {
    getTable();
  }, []);

  const uiLoader = () =>
    configKeys.map((key) => {
      const obj = data[key];
      const unit = obj["Unit"] !== "" ? ` (${obj["Unit"]})` : "";

      if (
        obj["status"] === "static" ||
        obj["status"] === "" ||
        obj["status"] === "finished"
      ) {
        if (obj["isImageVariable"] === true) {
          return (
            <ImageSelector
              label={obj["desc"]}
              data={data[key]}
              variable={key}
              onChange={updateValues()}
              key={key}
              disabled={obj["status"] === "finished"}
            ></ImageSelector>
          );
        } else {
          return (
            <div key={key + "WRAPPER"}>
              <Dropdown
                label={obj["desc"] + unit}
                placeholder="Select"
                data={data}
                variable={key}
                onChange={updateValues()}
                key={key}
                closeOnScroll={
                  screen === "isMobile" || screen === "isTablet" ? false : true
                }
              ></Dropdown>
            </div>
          );
        }
      } else if (obj["status"] === "ranged") {
        return (
          <div key={key + "WRAPPER"}>
            <Slider
              label={
                key === "PA" ? t("finder.pressure") + unit : obj["desc"] + unit
              }
              data={data[key]}
              variable={key}
              onChange={updateValues()}
              key={key}
            ></Slider>
            <div className={styles.sliderInputWrapper}>
              <Input
                width="128px"
                type="text"
                data={data[key]}
                variable={key}
                onChange={updateValues()}
              ></Input>
            </div>
          </div>
        );
      }
    });

  const toSeries = (item) => () => {
    navigate(`/${item.path}`, {
      state: { lines: item.lines },
    });
  };

  return (
    <div className={styles.container}>
      <BackButton></BackButton>
      <div
        className={`${styles.content} ${isDemoUser && styles.reducePadding}`}
      >
        {data ? (
          <>
            <h2>{t("finder.finderTitle")}</h2>
            <div className={styles.buttonWrapper}>
              <PatternlibButton
                label="Reset"
                type="secondary"
                onClick={resetValues()}
              ></PatternlibButton>
            </div>
            {uiLoader()}
            <Separator space=""></Separator>
            <h2>{t("finder.suggestionTitle")}</h2>
            {!isSearching ? (
              <ul className={styles.wrapper}>
                {nodes?.map((item, index) => {
                  let label = "";
                  let describtion = "";
                  let optionList = [];
                  let image = image260bar;

                  switch (item.path) {
                    case "series260":
                      label = t("finder.series260");
                      describtion = t("finder.describtionSeries260");
                      optionList = [
                        t("finder.series260Option1"),
                        t("finder.series260Option2"),
                      ];
                      image = image260bar;
                      break;
                    case "series380":
                      label = t("finder.series380");
                      describtion = t("finder.describtionSeries380");
                      optionList = [
                        t("finder.series380Option1"),
                        t("finder.series380Option2"),
                        t("finder.series380Option3"),
                      ];
                      image = image380bar;
                      break;
                    case "iso6022":
                      label = t("finder.seriesISO6022");
                      describtion = t("finder.describtionSeriesISO6022");
                      optionList = [
                        t("finder.seriesISO6022Option1"),
                        t("finder.seriesISO6022Option2"),
                        t("finder.seriesISO6022Option3"),
                        t("finder.seriesISO6022Option4"),
                      ];
                      image = imageIso6022;
                      break;
                    default:
                      label = t("finder.series260");
                      describtion = t("finder.describtionSeries260");
                      optionList = [
                        t("finder.series260Option1"),
                        t("finder.series260Option2"),
                      ];
                  }

                  return (
                    <li key={index} className={styles.detailsContainer}>
                      <img
                        className={styles.preview}
                        src={image}
                        onClick={toSeries(item)}
                      ></img>
                      <div className={styles.titleWrapper}>
                        <h2 className={styles.title} title={label}>
                          {label}
                        </h2>
                      </div>

                      <PatternlibButton
                        width="auto"
                        display="flex"
                        label={t("finder.start")}
                        icon-position="right"
                        onClick={toSeries(item)}
                      >
                        <PatternlibIcon
                          icon-name="arrow-right"
                          size="24px"
                          slot="icon"
                        />
                      </PatternlibButton>

                      <div className={styles.infoContainer}>
                        <span className={styles.bold}>{describtion}</span>
                        <Separator space="s"></Separator>
                        <h3>{t("finder.scopeTitle")}</h3>
                        <Separator space="xxs"></Separator>
                        <span>{t("finder.scopeDescribtion")}</span>
                        <Separator space="xxs"></Separator>
                        <ul className={styles.list}>
                          {optionList?.map((item) => {
                            return (
                              <li key={item}>
                                <span className={styles.icons}></span>
                                {item}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </li>
                  );
                })}
                {nodes.length === 0 && (
                  <div className={styles.noResultsWrapper}>
                    <span>{t("finder.empty")}</span>
                    <Separator space="xxs"></Separator>
                    <NotRightProductDiv
                      question={t("NotRightProductDiv.question2")}
                      support={t("NotRightProductDiv.support2")}
                    />
                  </div>
                )}
              </ul>
            ) : (
              <div className={styles.gridContainer}>
                <div className={styles.item1 && styles.gridItem}>
                  <PatternlibSpinner spin></PatternlibSpinner>
                </div>
                <div className={styles.item2 && styles.gridItem}>
                  {t("Spinner.loadProduct")}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={styles.gridContainer}>
            <div className={styles.item1 && styles.gridItem}>
              <PatternlibSpinner spin></PatternlibSpinner>
            </div>
            <div className={styles.item2 && styles.gridItem}>
              {t("Spinner.loadProduct")}
            </div>
          </div>
        )}
        <Separator space="xxs"></Separator>
        <p>{t("finder.finderDesc")}</p>
      </div>
    </div>
  );
};

export default Finder;
